import { DatePickerValue } from "../DatePicker";
import { OUTBOUND_UTM_SOURCE } from "./url";

export function formatDateTimeToISO(
  date: Date | null,
  time: string | null,
): string | null {
  if (!date) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  const timeStr = time && time.trim() !== "" ? time : "09:00";

  return `${year}-${month}-${day}T${timeStr}:00`;
}

export function formatDateToLocalISO(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:00.000`;
}

export function combineDateAndTime(
  date: Date | null,
  time: string | null,
): Date | null {
  if (!date) return null;

  const combined = new Date(date);

  if (time) {
    const parts = time.split(":");
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (!isNaN(hours) && !isNaN(minutes)) {
      combined.setHours(hours, minutes, 0, 0);
    }
  }

  return combined;
}

export function buildReservationUrl(
  locationId: string | undefined,
  datePickerValue: DatePickerValue,
): string {
  if (!locationId) return "#";

  const baseUrl = `https://app.parkmobile.io/reservation/${locationId}`;
  const params = new URLSearchParams();

  if (datePickerValue.startDate) {
    const startDateTime = formatDateTimeToISO(
      datePickerValue.startDate,
      datePickerValue.startTime,
    );
    if (startDateTime) {
      params.set("startDate", startDateTime);
    }
  }

  if (datePickerValue.endDate) {
    const endDateTime = formatDateTimeToISO(
      datePickerValue.endDate,
      datePickerValue.endTime,
    );
    if (endDateTime) {
      params.set("endDate", endDateTime);
    }
  }

  params.set("utm_source", OUTBOUND_UTM_SOURCE);
  return `${baseUrl}?${params.toString()}`;
}

function dateToReservationISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d}T${h}:${mi}:00`;
}

export function appendMatchDateToUrl(
  href: string | undefined,
  matchDate: string | undefined,
  durationHours = 3,
): string | undefined {
  if (!href || !matchDate) return href;
  if (href === "#" || href.startsWith("#")) return href;

  const start = new Date(matchDate);
  if (isNaN(start.getTime())) return href;
  const end = new Date(start.getTime() + durationHours * 3600 * 1000);

  try {
    const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(href);
    const url = isAbsolute
      ? new URL(href)
      : new URL(href, "http://placeholder.local");
    url.searchParams.set("startDate", dateToReservationISO(start));
    url.searchParams.set("endDate", dateToReservationISO(end));
    return isAbsolute
      ? url.toString()
      : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}
